import React, { useCallback, useEffect, useRef } from 'react';
import { styled, Text, Button, Box } from '@goodrx/matisse';
import { useSynapse } from 'app/lib/synaptik';
import { OnboardingField, OnboardingFieldProps } from './OnboardingField';
import { ServiceData } from '@goodrx/heydoctor-sdk/es/models/service';

const Wrapper = styled(Box)`
  opacity: 1;

  * {
    pointer-events: auto;
  }
`;

const GenderButton = styled(Button)`
  width: 50%;
`;

interface GenderProps extends OnboardingFieldProps {
  service?: ServiceData;
  hideDescription?: boolean;
}

const Gender: React.FunctionComponent<GenderProps> = ({ onChange, values, error, service, hideDescription }) => {
  const handleChange = useCallback(
    (gender: 'male' | 'female') => {
      onChange({
        values: { gender },
        validate: () => {
          if (service && service.gender !== 'all' && gender && service.gender !== gender) {
            return {
              valid: false,
              errorMessage: `You must be ${service.gender} to use this service`,
            };
          }

          return { valid: true };
        },
      });
    },
    [service, onChange]
  );

  const handleSelectFemale = useCallback(() => handleChange('female'), [handleChange]);
  const handleSelectMale = useCallback(() => handleChange('male'), [handleChange]);

  const handleServiceChangeRef = useRef(useCallback(() => handleChange(values.gender), [values.gender, handleChange]));

  useEffect(() => {
    handleServiceChangeRef.current();
  }, [service]);

  return (
    <Wrapper>
      <Text as="h3" fontSize={18} mt={3} mb={2}>
        Biological sex
      </Text>
      {!hideDescription && (
        <Text as="p" mt={0} mb={3} fontSize={16} fontWeight="500" color="greyDarkest">
          This helps us provide tailored treatment plans.
        </Text>
      )}

      <Button.Group connected>
        <GenderButton variant={values.gender === 'male' ? 'primary' : 'secondary'} onClick={handleSelectMale}>
          Male
        </GenderButton>
        <GenderButton variant={values.gender === 'female' ? 'primary' : 'secondary'} onClick={handleSelectFemale}>
          Female
        </GenderButton>
      </Button.Group>
      {error && (
        <Text as="p" color="red" fontWeight={500} mt="2" fontSize="14px">
          {error}
        </Text>
      )}
    </Wrapper>
  );
};

const GenderWrapper: React.FunctionComponent<{ service?: ServiceData; hideDescription?: boolean }> = ({
  hideDescription,
  service: passedService,
}) => {
  const service = useSynapse(({ services }) => passedService || services.getIntendedService());

  return (
    <OnboardingField errorFieldName="gender" fields={['gender']}>
      {(props) => <Gender {...props} service={service} hideDescription={hideDescription} />}
    </OnboardingField>
  );
};

export { GenderWrapper as Gender };
